/**
 * A custom element that clamps rich text to a fixed number of lines and
 * reveals a "show more/less" toggle only when the text actually overflows.
 */
class ReadMoreText extends HTMLElement {
  #controller = new AbortController();

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    const { signal } = this.#controller;
    const content = this.querySelector(':scope > .text-block');
    const button = this.querySelector(':scope > .read-more-text__toggle');

    if (!(content instanceof HTMLElement) || !(button instanceof HTMLElement)) return;

    button.addEventListener('click', () => this.#toggle(content, button), { signal });

    this.#resizeObserver = new ResizeObserver(() => this.#syncOverflow(content, button));
    this.#resizeObserver.observe(content);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Shows the toggle only when the clamped content is actually overflowing.
   * Skipped while expanded, since the content then intentionally fills its
   * natural (unclamped) height.
   * @param {HTMLElement} content
   * @param {HTMLElement} button
   */
  #syncOverflow(content, button) {
    if (this.dataset.expanded === 'true') return;

    button.hidden = content.scrollHeight <= content.clientHeight + 1;
  }

  /**
   * @param {HTMLElement} content
   * @param {HTMLElement} button
   */
  #toggle(content, button) {
    const expanded = this.dataset.expanded === 'true';

    this.dataset.expanded = expanded ? 'false' : 'true';
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');

    if (expanded) this.#syncOverflow(content, button);
  }
}

if (!customElements.get('read-more-text')) {
  customElements.define('read-more-text', ReadMoreText);
}
